'use client';

import { BarChart as MuiBarChart } from '@mui/x-charts/BarChart';

import { DEFAULT_CHART_COLORS } from './chartColors';

const AXIS_TICK_LABEL_STYLE = {
  fill: 'var(--nn-color-text)',
  fontSize: 12,
  fontWeight: 600,
} as const;

const HIDDEN_AXIS_TICK_LABEL_STYLE = {
  display: 'none',
} as const;

export type BarChartDatum = {
  label: string;
  value: number | null;
  color?: string;
};

type BarChartProps = {
  data: BarChartDatum[];
  height?: number;
  valueLabel?: string;
  className?: string;
  hideAxis?: boolean;
  ariaLabel?: string;
};

export function BarChart({
  data,
  height = 220,
  valueLabel = 'Value',
  className,
  hideAxis = false,
  ariaLabel,
}: BarChartProps) {
  const colors = data.map(
    (item, index) =>
      item.color ?? DEFAULT_CHART_COLORS[index % DEFAULT_CHART_COLORS.length],
  );
  const values = data.map((item) => item.value ?? 0);
  const labels = data.map((item) => item.label);

  return (
    <div
      className={className}
      aria-label={ariaLabel}
      style={{ minWidth: 0, overflow: 'hidden', width: '100%' }}
    >
      <MuiBarChart
        height={height}
        margin={{ top: 24, right: 8, bottom: hideAxis ? 8 : 34, left: 8 }}
        colors={colors as unknown as string[]}
        slotProps={{
          bar: (ownerState: { dataIndex?: number }) => ({
            style: {
              fill: colors[
                (ownerState.dataIndex ?? 0) % Math.max(colors.length, 1)
              ],
            },
          }),
        }}
        series={[
          {
            data: values,
            label: valueLabel,
          },
        ]}
        xAxis={[
          {
            data: labels,
            colorMap: {
              type: 'ordinal',
              values: labels,
              colors,
              unknownColor: DEFAULT_CHART_COLORS[0],
            },
            scaleType: 'band',
            tickLabelStyle: hideAxis
              ? HIDDEN_AXIS_TICK_LABEL_STYLE
              : AXIS_TICK_LABEL_STYLE,
          },
        ]}
        yAxis={[
          {
            tickLabelStyle: HIDDEN_AXIS_TICK_LABEL_STYLE,
          },
        ]}
        hideLegend
        grid={undefined}
        sx={{
          '& .MuiChartsAxis-line, & .MuiChartsAxis-tick': {
            display: 'none',
          },
          '& .MuiChartsAxis-tickLabel, & .MuiChartsText-root': {
            color: 'var(--nn-color-text) !important',
            fill: 'var(--nn-color-text) !important',
          },
          '& text, & tspan': {
            color: 'var(--nn-color-text) !important',
            fill: 'var(--nn-color-text) !important',
            stroke: 'none !important',
          },
        }}
      />
    </div>
  );
}
